import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { assessmentService } from '../services/assessmentService';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Loader2, Mail, User, Lock, CheckCircle2, Eye, EyeOff, X } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';

const RegistrationPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { language } = useLanguage();
  const responses = location.state?.responses || {};
  const passedLanguage = location.state?.language || language;

  const [loading, setLoading] = useState(false);
  const [showExistingEmailModal, setShowExistingEmailModal] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState(
    location.state?.formData || {
      first_name: '',
      last_name: '',
      email: '',
      create_account: false,
      password: '',
      confirm_password: ''
    }
  );
  const [errors, setErrors] = useState({});

  const texts = {
    en: {
      step1: 'Questionnaire Complete',
      step2: 'Get Results',
      almostDone: 'Almost Done! Get Your Results',
      enterInfo: 'Enter your information to receive your personalized well-being report via email',
      firstName: 'First Name',
      lastName: 'Last Name',
      emailAddress: 'Email Address',
      reportSentTo: 'Your report will be sent to this email address',
      createAccount: 'Create an account to save your results',
      createAccountDesc: 'Track your progress over time and retake the questionnaire to see how you improve',
      createPassword: 'Create Password',
      confirmPassword: 'Confirm Password',
      atLeast8: 'At least 8 characters',
      reenter: 'Re-enter password',
      privacyNotice: 'Privacy Notice',
      privacyText: 'Your information will be used solely to deliver your questionnaire results. We will not share your information with third parties.',
      getResults: 'Get My Results',
      processing: 'Processing...',
      required: 'is required',
      invalidEmail: 'Please enter a valid email address',
      passwordLength: 'Password must be at least 8 characters',
      passwordMatch: 'Passwords do not match',
      fixErrors: 'Please fix the errors before submitting',
      noResponses: 'No questionnaire responses found. Please complete the questionnaire first.',
      startQuestionnaire: 'Start Questionnaire',
      success: 'Questionnaire completed successfully!',
      existingEmailTitle: 'Email already used',
      existingEmailMessage: 'The email is already used. Please login or select a new email address.',
      loginBtn: 'Login',
      useDifferentEmail: 'Use different email'
    },
    fr: {
      step1: 'Questionnaire terminé',
      step2: 'Obtenir les résultats',
      almostDone: 'Presque terminé! Obtenez vos résultats',
      enterInfo: 'Entrez vos coordonnées pour recevoir votre rapport de bien-être personnalisé par courriel',
      firstName: 'Prénom',
      lastName: 'Nom',
      emailAddress: 'Adresse courriel',
      reportSentTo: 'Votre rapport sera envoyé à cette adresse courriel',
      createAccount: 'Créer un compte pour enregistrer vos résultats',
      createAccountDesc: 'Suivez vos progrès au fil du temps et reprenez l\'auto-évaluation pour voir comment vous vous améliorez',
      createPassword: 'Créer un mot de passe',
      confirmPassword: 'Confirmer le mot de passe',
      atLeast8: 'Au moins 8 caractères',
      reenter: 'Ressaisir le mot de passe',
      privacyNotice: 'Avis de confidentialité',
      privacyText: 'Vos informations seront utilisées uniquement pour vous transmettre les résultats de l\'auto-évaluation. Nous ne partagerons pas vos informations avec des tiers.',
      getResults: 'Obtenir mes résultats',
      processing: 'Traitement en cours...',
      required: 'est requis',
      invalidEmail: 'Veuillez entrer une adresse courriel valide',
      passwordLength: 'Le mot de passe doit contenir au moins 8 caractères',
      passwordMatch: 'Les mots de passe ne correspondent pas',
      fixErrors: 'Veuillez corriger les erreurs avant de soumettre',
      noResponses: 'Aucune réponse à l\'auto-évaluation trouvée. Veuillez d\'abord compléter l\'auto-évaluation.',
      startQuestionnaire: 'Commencer l\'auto-évaluation',
      success: 'Auto-évaluation complétée avec succès!',
      existingEmailTitle: 'Courriel déjà utilisé',
      existingEmailMessage: 'Le courriel est déjà utilisé. Veuillez vous connecter ou choisir une autre adresse courriel.',
      loginBtn: 'Se connecter',
      useDifferentEmail: 'Utiliser un autre courriel'
    }
  };

  const txt = texts[passedLanguage] || texts.en;

  const getCopyrightText = () => {
    const yearSuffix = String(new Date().getFullYear()).slice(-2);
    return passedLanguage === 'fr'
      ? `© 2020-${yearSuffix} Centre des points forts, une division de GABCM Inc. Tous droits réservés.`
      : `© 2020-${yearSuffix} Strengths Performance Centre, a division of GABCM Inc. All rights reserved.`;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.first_name.trim()) {
      newErrors.first_name = `${txt.firstName} ${txt.required}`;
    }
    if (!formData.last_name.trim()) {
      newErrors.last_name = `${txt.lastName} ${txt.required}`;
    }
    if (!formData.email.trim()) {
      newErrors.email = `${txt.emailAddress} ${txt.required}`;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = txt.invalidEmail;
    }

    if (formData.create_account) {
      if (!formData.password) {
        newErrors.password = `${txt.createPassword} ${txt.required}`;
      } else if (formData.password.length < 8) {
        newErrors.password = txt.passwordLength;
      }

      if (formData.password !== formData.confirm_password) {
        newErrors.confirm_password = txt.passwordMatch;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildFormattedResponses = () => {
    return Object.entries(responses).map(([questionId, value]) => ({
      question_id: questionId,
      response_value: value
    }));
  };

  const handleExistingEmailLogin = () => {
    const submissionPayload = {
      first_name: formData.first_name,
      last_name: formData.last_name,
      email: formData.email,
      responses: buildFormattedResponses(),
      create_account: false,
      language: passedLanguage
    };

    navigate('/login', {
      state: {
        pendingAssessmentSubmission: {
          submissionPayload,
          rawResponses: responses,
          language: passedLanguage,
          formData: { ...formData }
        },
        backTo: '/assessment/register',
        backState: {
          responses,
          language: passedLanguage,
          formData: { ...formData }
        }
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error(txt.fixErrors);
      return;
    }

    setLoading(true);

    try {
      const formattedResponses = buildFormattedResponses();

      const result = await assessmentService.submitCompletion({
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        responses: formattedResponses,
        create_account: formData.create_account,
        password: formData.create_account ? formData.password : undefined,
        language: passedLanguage
      });

      toast.success(txt.success);

      navigate('/assessment/results', {
        state: {
          result,
          email: formData.email,
          createdAccount: formData.create_account,
          language: passedLanguage
        }
      });
    } catch (error) {
      const errorDetail = error.response?.data?.detail || '';

      if (
        formData.create_account &&
        (errorDetail.includes('Email already registered') || errorDetail.includes('already exists'))
      ) {
        setShowExistingEmailModal(true);
      } else {
        console.error('Submission error:', error);
        toast.error(
          errorDetail ||
            (passedLanguage === 'fr'
              ? 'Échec de la soumission. Veuillez réessayer.'
              : 'Failed to submit. Please try again.')
        );
      }
    } finally {
      setLoading(false);
    }
  };

  if (!responses || Object.keys(responses).length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-white via-brand-50/20 to-white">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <p className="text-gray-600 mb-4">{txt.noResponses}</p>
            <Button onClick={() => navigate('/assessment/start')}>
              {txt.startQuestionnaire}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-brand-50/20 to-white py-12">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-center gap-4 mb-8">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-brand-600 rounded-full flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-white" />
            </div>
            <span className="font-medium text-brand-600">{txt.step1}</span>
          </div>
          <div className="w-12 h-1 bg-brand-600 rounded"></div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-brand-600 rounded-full flex items-center justify-center text-white font-bold">
              2
            </div>
            <span className="font-medium text-brand-600">{txt.step2}</span>
          </div>
        </div>

        <Card className="shadow-xl">
          <CardHeader className="bg-gradient-to-br from-brand-50 to-white border-b border-gray-100">
            <CardTitle className="text-2xl font-black text-gray-900">{txt.almostDone}</CardTitle>
            <CardDescription className="text-gray-600">
              {txt.enterInfo}
            </CardDescription>
          </CardHeader>

          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name" className="flex items-center gap-1">
                    {txt.firstName} <span className="text-red-500">*</span>
                  </Label>
                  <div className="relative">
                    <User className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      id="first_name"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleInputChange}
                      className={`pl-10 ${errors.first_name ? 'border-red-500' : ''}`}
                      placeholder="John"
                      data-testid="first-name-input"
                    />
                  </div>
                  {errors.first_name && <p className="text-red-500 text-sm">{errors.first_name}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="last_name" className="flex items-center gap-1">
                    {txt.lastName} <span className="text-red-500">*</span>
                  </Label>
                  <div className="relative">
                    <User className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      id="last_name"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleInputChange}
                      className={`pl-10 ${errors.last_name ? 'border-red-500' : ''}`}
                      placeholder="Doe"
                      data-testid="last-name-input"
                    />
                  </div>
                  {errors.last_name && <p className="text-red-500 text-sm">{errors.last_name}</p>}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="flex items-center gap-1">
                  {txt.emailAddress} <span className="text-red-500">*</span>
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`pl-10 ${errors.email ? 'border-red-500' : ''}`}
                    placeholder="john.doe@example.com"
                    data-testid="email-input"
                  />
                </div>
                {errors.email && <p className="text-red-500 text-sm">{errors.email}</p>}
                <p className="text-sm text-gray-500">{txt.reportSentTo}</p>
              </div>

              <div className="bg-brand-50 rounded-lg p-4 space-y-4">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="create_account"
                    name="create_account"
                    checked={formData.create_account}
                    onCheckedChange={(checked) => {
                      setFormData((prev) => ({
                        ...prev,
                        create_account: !!checked
                      }));
                    }}
                    className="mt-1"
                  />
                  <div>
                    <Label htmlFor="create_account" className="font-semibold text-gray-900 cursor-pointer">
                      {txt.createAccount}
                    </Label>
                    <p className="text-sm text-gray-600">{txt.createAccountDesc}</p>
                  </div>
                </div>

                {formData.create_account && (
                  <div className="space-y-4 pt-4 border-t border-brand-200">
                    <div className="space-y-2">
                      <Label htmlFor="password">{txt.createPassword}</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                        <Input
                          id="password"
                          name="password"
                          type={showPassword ? 'text' : 'password'}
                          value={formData.password}
                          onChange={handleInputChange}
                          className={`pl-10 pr-10 ${errors.password ? 'border-red-500' : ''}`}
                          placeholder={txt.atLeast8}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                      {errors.password && <p className="text-red-500 text-sm">{errors.password}</p>}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="confirm_password">{txt.confirmPassword}</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                        <Input
                          id="confirm_password"
                          name="confirm_password"
                          type={showConfirmPassword ? 'text' : 'password'}
                          value={formData.confirm_password}
                          onChange={handleInputChange}
                          className={`pl-10 pr-10 ${errors.confirm_password ? 'border-red-500' : ''}`}
                          placeholder={txt.reenter}
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                        >
                          {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                      {errors.confirm_password && <p className="text-red-500 text-sm">{errors.confirm_password}</p>}
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">{txt.privacyNotice}</h4>
                <p className="text-sm text-gray-600">{txt.privacyText}</p>
              </div>

              <Button
                type="submit"
                size="lg"
                className="w-full bg-brand-600 hover:bg-brand-700"
                disabled={loading}
                data-testid="submit-assessment-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    {txt.processing}
                  </>
                ) : (
                  txt.getResults
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        <div className="mt-6 text-center text-xs text-gray-500">
          <p>{getCopyrightText()}</p>
        </div>
      </div>

      {showExistingEmailModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md shadow-2xl">
            <CardHeader className="relative">
              <button
                type="button"
                onClick={() => setShowExistingEmailModal(false)}
                className="absolute right-4 top-4 text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
              <CardTitle className="text-xl font-black text-gray-900">{txt.existingEmailTitle}</CardTitle>
              <CardDescription>{txt.existingEmailMessage}</CardDescription>
            </CardHeader>

            <CardContent className="pt-2">
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowExistingEmailModal(false)}
                >
                  {txt.useDifferentEmail}
                </Button>
                <Button
                  className="flex-1 bg-brand-600 hover:bg-brand-700"
                  onClick={handleExistingEmailLogin}
                >
                  {txt.loginBtn}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default RegistrationPage;
